/**
 * What an artefact is called when it lands on someone's disk.
 *
 * The manifest's `storageKey` is the contract's name for an artefact and does
 * not change: `drawing.dxf` is what the pipeline produced, what its sha256 is
 * keyed to, and what the FILES fold lists. This is the other name — the one the
 * browser writes — and it exists because those two jobs are not the same job.
 *
 * Every conversion downloaded `drawing.dxf`. Convert five revisions of one part
 * and the Downloads folder holds `drawing.dxf`, `drawing (1).dxf`,
 * `drawing (2).dxf`, in an order only the clock knows. In a drawing office the
 * filename *is* the revision, so the artefact lost its identity at the exact
 * moment it left the tool — the one point in the journey where nothing else can
 * put it back.
 */

/** The deliverable takes the drawing's name outright; everything else is prefixed. */
const DELIVERABLE = 'drawing.dxf';

export function downloadNameFor(storageKey: string, sourceName: string | null): string {
  const stem = stemOf(sourceName);
  if (stem === null) return storageKey;
  return storageKey === DELIVERABLE ? `${stem}.dxf` : `${stem}.${storageKey}`;
}

/**
 * The drawing's name with its directory and its `.pdf` removed.
 *
 * Only a trailing `.pdf` is stripped, not "the last extension": `A-001.rev3.pdf`
 * has to stay `A-001.rev3`, because that dot is carrying the revision and
 * dropping it is worse than any tidiness gained. Separators go because the name
 * is handed to a download attribute, where a `/` is a path rather than a
 * character.
 *
 * Returns null when what is left could not name a file, so the caller falls
 * back to the contract key rather than writing something called `.dxf`.
 */
function stemOf(sourceName: string | null): string | null {
  if (sourceName === null) return null;
  const bare = sourceName.split(/[/\\]/).pop() ?? '';
  const stem = bare.replace(/\.pdf$/i, '').trim();
  return stem === '' ? null : stem;
}
